import React from "react";
import { AlertTriangle, CheckCircle2, CircleDashed } from "lucide-react";
import RadarChart from "./RadarChart.jsx";
import {
  BASICS_PASS,
  NOT_SCORING_YET,
  buildBasicsStanding,
} from "./basicsFloor.js";
import { scoreColor } from "./callMeta.js";

// Checkpoint 1 UI: basics standing + floor-only radar + green/red chips.
// Growth rungs 1–10 only in copy — no letter-codes, no “layer” words.

const card = {
  background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14,
  padding: "16px 18px",
};
const muted = { color: "var(--c-500)" };

function Chip({ tone, children }) {
  const styles = {
    green: {
      border: "1px solid var(--green-600)",
      color: "var(--green-700, var(--green-600))",
      background: "rgba(46, 160, 67, 0.08)",
    },
    red: {
      border: "1px solid var(--red-600, #c0392b)",
      color: "var(--red-600, #c0392b)",
      background: "rgba(192, 57, 43, 0.06)",
    },
    amber: {
      border: "1px solid var(--amber-600)",
      color: "var(--amber-700, var(--amber-600))",
      background: "rgba(217, 164, 65, 0.1)",
    },
  }[tone] || {};
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999,
      ...styles,
    }}>
      {children}
    </span>
  );
}

function LineRow({ line, onOpenProof }) {
  const color = line.status === "not_measured"
    ? "var(--c-400)"
    : scoreColor(line.score);
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr auto", gap: 10,
      padding: "8px 0", borderTop: "1px solid var(--c-200)", alignItems: "baseline",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--c-900)" }}>
          {line.label}
        </div>
        {line.status === "not_measured" && (
          <div style={{ fontSize: 11.5, ...muted, marginTop: 2 }}>Not measured yet</div>
        )}
        {line.proof && line.status === "fail" && (
          <button
            type="button"
            onClick={() => onOpenProof && onOpenProof(line.proof.callId)}
            style={{
              marginTop: 3, border: "none", background: "transparent", padding: 0,
              cursor: "pointer", fontSize: 11.5, color: "var(--blue-600)", fontWeight: 600,
              textAlign: "left",
            }}
          >
            {[line.proof.company, line.proof.date].filter(Boolean).join(" · ") || "Open proof call"}
            {line.proof.heard ? ` — “${String(line.proof.heard).slice(0, 72)}”` : ""}
          </button>
        )}
        {line.measureHint && line.status !== "not_measured" && (
          <div style={{ fontSize: 11, ...muted, marginTop: 2 }}>
            {line.measureHint.id === "filler_per_100w"
              ? `${line.measureHint.value} fillers / 100 words (latest measured)`
              : line.measureHint.id === "speaking_pace_wpm"
                ? `${line.measureHint.value} words / min (latest measured)`
                : null}
          </div>
        )}
      </div>
      <div style={{
        fontSize: 16, fontWeight: 700, color, fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}>
        {line.status === "not_measured" ? "—" : `${line.score}`}
        {line.status !== "not_measured" && (
          <span style={{ fontSize: 11, fontWeight: 500, ...muted }}> /10</span>
        )}
      </div>
    </div>
  );
}

export default function BasicsFloor({
  calls,
  detailsById = {},
  onOpenCall,
}) {
  const standing = buildBasicsStanding(calls, detailsById);
  const gateFail = standing.failing.length > 0
    || (standing.floorAvg != null && standing.floorAvg < BASICS_PASS);

  return (
    <div style={{ ...card, marginBottom: 12 }}>
      <div style={{
        display: "flex", justifyContent: "space-between", gap: 12,
        alignItems: "flex-start", flexWrap: "wrap", marginBottom: 6,
      }}>
        <div>
          <div style={{
            fontSize: 11.5, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: ".04em", color: "var(--c-600)",
          }}>
            Basics floor
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 12.5, ...muted, maxWidth: 420 }}>
            {standing.windowLabel}. Pass mark {BASICS_PASS}/10 — changeable later.
            Every call still gets a daily read; this standing ignores odd one-off spikes.
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{
            fontSize: 28, fontWeight: 800, fontVariantNumeric: "tabular-nums",
            color: standing.floorAvg == null ? "var(--c-400)" : scoreColor(standing.floorAvg),
            lineHeight: 1,
          }}>
            {standing.floorAvg ?? "—"}
          </div>
          <div style={{ fontSize: 11, ...muted, marginTop: 2 }}>
            {gateFail ? "Fix basics first" : standing.n ? "Floor clearing" : "Waiting on calls"}
          </div>
        </div>
      </div>

      {standing.anomalyCount > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: 6, fontSize: 12,
          color: "var(--amber-700, var(--amber-600))", marginBottom: 8,
        }}>
          <AlertTriangle size={13} />
          {standing.anomalyCount} odd reading{standing.anomalyCount === 1 ? "" : "s"} soft-dropped
          from the standing (still on the call list).
        </div>
      )}

      <div style={{
        display: "grid",
        gridTemplateColumns: standing.radarAxes.length >= 3
          ? "minmax(0, 1fr) minmax(200px, 240px)"
          : "1fr",
        gap: 12, alignItems: "start",
      }}>
        <div>
          {standing.lines.map((line) => (
            <LineRow key={line.id} line={line} onOpenProof={onOpenCall} />
          ))}
        </div>
        {standing.radarAxes.length >= 3 && (
          <div style={{ justifySelf: "center" }}>
            <RadarChart axes={standing.radarAxes} size={220} />
            <p style={{ margin: "4px 0 0", fontSize: 10.5, ...muted, textAlign: "center" }}>
              Basics only — nothing else on this radar
            </p>
          </div>
        )}
        {standing.radarAxes.length > 0 && standing.radarAxes.length < 3 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {standing.radarAxes.map((a) => (
              <div key={a.key} style={{
                display: "flex", justifyContent: "space-between", fontSize: 12.5,
                padding: "4px 0", borderBottom: "1px solid var(--c-200)",
              }}>
                <span>{a.label}</span>
                <strong style={{ color: scoreColor(a.score), fontVariantNumeric: "tabular-nums" }}>
                  {a.score}
                </strong>
              </div>
            ))}
            <p style={{ fontSize: 11, ...muted, margin: 0 }}>
              Bars until three basics axes exist for a radar.
            </p>
          </div>
        )}
      </div>

      <div style={{
        marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--c-200)",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <div>
          <div style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em",
            textTransform: "uppercase", ...muted, marginBottom: 6,
          }}>
            You&apos;ve got these
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {standing.passing.length === 0 && (
              <Chip tone="amber">
                <CircleDashed size={11} /> Nothing solid on the floor yet
              </Chip>
            )}
            {standing.passing.map((l) => (
              <Chip key={l.id} tone="green">
                <CheckCircle2 size={11} /> {l.label}
              </Chip>
            ))}
          </div>
          {standing.passing.length > 0 && (
            <p style={{ margin: "6px 0 0", fontSize: 11.5, ...muted }}>
              From earlier work on the basics — keep it.
            </p>
          )}
        </div>

        <div>
          <div style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em",
            textTransform: "uppercase", ...muted, marginBottom: 6,
          }}>
            Not scoring yet
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {standing.lines.filter((l) => l.status === "not_measured").map((l) => (
              <Chip key={`nm-${l.id}`} tone="red">{l.label} · not measured</Chip>
            ))}
            {NOT_SCORING_YET.map((item) => (
              <Chip key={item.id} tone="red">{item.label}</Chip>
            ))}
          </div>
          <p style={{ margin: "6px 0 0", fontSize: 11.5, ...muted }}>
            Planned for later checkpoints — not on the radar, not in the standing.
          </p>
        </div>
      </div>
    </div>
  );
}
